import Box from '@mui/material/Box';
import { RefreshButton } from 'components';
import { getTablesTheme } from 'components/CustomThemes';
import { lighten, ThemeProvider } from '@mui/material/styles';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleGlobalFilterButton,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from 'material-react-table';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import { TABLE_LOCALE } from 'shared/constants/tableOptions';
import { USER_DATA } from 'shared/constants/users';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';

const propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  darkMode: PropTypes.bool,
  handleRefreshClick: PropTypes.func.isRequired,
  tableTitle: PropTypes.string.isRequired,
  enableRowSelection: PropTypes.bool,
  customToolbar: PropTypes.node,
  rowsPerPageOptions: PropTypes.array,
};

const BasicTable = ({
  columns,
  data,
  darkMode = false,
  handleRefreshClick,
  enableRowSelection = false,
  tableTitle,
  customToolbar,
  rowsPerPageOptions = [10, 20, 50, 100],
}) => {
  const locale = localStorage.getItem(USER_DATA.PREFFERED_LANGUAGE);

  const table = useMaterialReactTable({
    columns,
    data,
    localization: TABLE_LOCALE[locale],
    muiTableContainerProps: {
      sx: { maxHeight: '950px' },
    },
    // !TODO Dark Mode
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    enableColumnFilterModes: false,
    enableGrouping: true,
    enableColumnDragging: false,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableRowSelection: enableRowSelection,
    paginationDisplayMode: 'pages',
    enableMultiRowSelection: false,
    positionToolbarAlertBanner: 'bottom',
    autoResetPageIndex: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
      density: 'compact',
    },
    muiPaginationProps: {
      rowsPerPageOptions: rowsPerPageOptions,
    },
    state: { isLoading: !data },
    renderTopToolbar: ({ table }) => {
      return (
        <Stack sx={{ m: '2rem 0' }}>
          <Box sx={{ pl: '10px' }}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {tableTitle}
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: lighten(theme.palette.background.default, 0.05),
              display: 'flex',
              gap: '0.5rem',
              p: '8px',
              justifyContent: customToolbar ? 'space-between' : 'flex-end',
            })}>
            {customToolbar}
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {}
              <MRT_GlobalFilterTextField table={table} />
              <MRT_ToggleGlobalFilterButton table={table} />
              <MRT_ShowHideColumnsButton table={table} />
              <RefreshButton
                darkMode={darkMode}
                tooltipTitle={intl.get(
                  'dashboard_refresh_button_tooltip_title'
                )}
                handleRefreshClick={() => {
                  handleRefreshClick();
                  table.resetRowSelection();
                }}
              />
            </Box>
          </Box>
        </Stack>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: {
        cursor: 'pointer',
      },
    }),
  });

  return (
    <Box>
      <ThemeProvider theme={getTablesTheme(darkMode)}>
        <MaterialReactTable table={table} />
      </ThemeProvider>
    </Box>
  );
};

BasicTable.propTypes = propTypes;

export default BasicTable;
